import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useHistory } from 'react-router-dom'

import {
    AppContainer,
    AppRouter,
    ServiceUsageController,
    UploadProgressPopup,
    MenuStatusProvider,
    OnboardingDialog
} from 'UI/Components'
import { SessionExpiredDialog } from 'UI/Routes/domain-redirect/SessionExpiredDialog'

import { resetAppAuth, updateAppAuth, UpdateAuthPayload } from 'ducks/actions'

import {
    envConfig,
    registerToFirebaseAuth,
    createGapi,
    registerUserInAnalytics,
    detectBrowserWithVersion,
    getOS,
    checkIfExtIsConnected,
    isDev
} from 'modules'
import {
    useAuth,
    useGlobalFeatureFlags,
    useOrgFeatureFlags,
    useQuery,
    useServiceUsage
} from 'hooks'
import { paths } from 'app/paths'
import { LocationDescriptor } from 'history'

const App = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const { showPayments } = useOrgFeatureFlags()

    const historyPush = useHistory().push

    useGlobalFeatureFlags()

    const { uid, displayName, email, roles, orgId, isLoading } = useAuth()
    const { activePlanId } = useServiceUsage()

    const { data: hash, isValidating } = useQuery<{ data: string }>(
        '/c/v1/intercom/identityVerification',
        {
            method: 'POST'
        }
    )

    useEffect(() => {
        window.addEventListener('focus', checkIfExtIsConnected)

        checkIfExtIsConnected()
    }, [])

    useEffect(() => {
        if (isValidating || !hash || !orgId) return
        ;(window as any)?.Intercom?.('boot', {
            api_base: 'https://api-iam.intercom.io',
            app_id: isDev ? 'be1vcijb' : 'ejamtgjy',
            name: displayName,
            email: email,
            user_id: uid,
            org_id: orgId,
            user_hash: hash.data // HMAC using SHA-256
        })
    }, [displayName, email, uid, isValidating, hash, orgId])

    // send Intercom an update event on any route change
    useEffect(() => {
        ;(window as any)?.Intercom?.('update', {
            user_id: uid,
            location: location.pathname,
            last_request_at: parseInt(String(new Date().getTime() / 1000))
        })
    }, [location, uid])

    // Run on App Init
    useEffect(() => {
        registerToFirebaseAuth((payload: UpdateAuthPayload | null) => {
            if (payload) {
                if (payload.roles?.n && (payload.roles.a > 10 || !payload.roles.o)) {
                    const data: LocationDescriptor = {
                        pathname: paths.onboarding,
                        search: window.location.search
                    }

                    historyPush(data)
                }

                dispatch(updateAppAuth(payload))
            } else {
                dispatch(resetAppAuth())
            }
        })
        createGapi()
    }, [dispatch, historyPush])

    useEffect(() => {
        if (uid) {
            try {
                window.guidde?.init({
                    userId: uid, // unique
                    userEmail: email,
                    userName: displayName
                })
            } catch (error) {
                console.error(error)
            }
        }
    }, [uid, displayName, email, roles])

    useEffect(() => {
        if (uid) {
            // Identify user in Sentry + FullStory + Mixpanel with the same props
            registerUserInAnalytics({
                plan: activePlanId || '',
                uid,
                displayName,
                email,
                orgId: roles?.o || '',
                env: envConfig.firebaseConfig.projectId,
                ...detectBrowserWithVersion(),
                ...getOS()
            })
        }
    }, [activePlanId, displayName, email, roles, uid])

    if (isLoading) return null

    return (
        <MenuStatusProvider>
            <AppContainer>
                <SessionExpiredDialog />
                {showPayments && <ServiceUsageController />}
                <UploadProgressPopup />
                <OnboardingDialog />
                <AppRouter />
            </AppContainer>
        </MenuStatusProvider>
    )
}

export default App
